import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faArrowRightArrowLeft,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';

import {
  projectTutorialSteps,
  type TutorialPage,
  type TutorialStep,
} from './projectTutorialSteps';

interface ProjectTutorialProps {
  /** When true the sequential tour is shown. */
  open: boolean;
  /** Called when the tour is finished, skipped, or dismissed (Escape /
   *  close button). The parent decides whether to also mark the tutorial
   *  as seen — marking is idempotent, so it's safe to always do so. */
  onClose: () => void;
  /** `xml` describes engraved pitch modes. Scan projects use `pdf`. */
  variant?: TutorialPage;
}

/** Pixels of breathing room drawn around the highlighted control. */
const SPOTLIGHT_PAD = 10;
/** Card width (px) — used for left/right placement maths. Keep in sync
 *  with the `w-[24rem]` on the card element below (24rem = 384px). */
const CARD_WIDTH = 384;
/** Gap between the spotlight and the card. */
const CARD_GAP = 16;
/** Keep the card this far from the viewport edges. */
const VIEWPORT_MARGIN = 12;
/** Desktop card height ceiling. Keep in sync with `max-h-[70vh]` /
 *  `md:max-h-[min(70vh,40rem)]` on the card (40rem = 640px). */
const CARD_MAX_HEIGHT_VH = 0.7;
const CARD_MAX_HEIGHT_PX = 640;
/** Shortest useful card: header, title, one full illustration (h-28),
 *  and the Skip/Next footer. A lower spotlight on a short window slides
 *  the card up to this height so the body is not a cropped sliver.
 *  Shorter viewports still cap this at the space that actually fits. */
const CARD_MIN_HEIGHT = 500;
/** Minimum thumb height (px) for the always-visible custom scrollbar. */
const SCROLL_THUMB_MIN = 28;

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface ScrollMetrics {
  /** True when the body content overflows its container. */
  overflow: boolean;
  /** True when there is more content below the current scroll position. */
  canScrollDown: boolean;
  /** Custom thumb geometry as percentages of the track (0–100). */
  thumbTopPct: number;
  thumbHeightPct: number;
}

const measureTarget = (target: string): Rect | null => {
  // A control can be tagged in TWO places: the desktop chrome (top
  // toolbar + right-gutter toggles, `hidden md:*`) and the mobile
  // bottom bar (`md:hidden`). Only one set is visible at a time, but
  // both are in the DOM, so we scan ALL matches and take the first with
  // a non-zero box — otherwise `querySelector` would return the hidden
  // desktop copy first on small screens and every step would be skipped.
  const els = Array.from(
    document.querySelectorAll<HTMLElement>(`[data-tour="${target}"]`)
  );
  for (const el of els) {
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue;
    return { top: r.top, left: r.left, width: r.width, height: r.height };
  }
  return null;
};

const isVideo = (src: string) => /\.(mp4|webm|ogg)$/i.test(src);

/**
 * Sequential, spotlight-style onboarding tour for the PDF project page.
 *
 * It highlights existing controls (tagged with `data-tour="…"` on the
 * `ScoreSideToggles` pills and `ProjectToolbar` buttons) one at a time,
 * dimming the rest of the page and anchoring an explanatory card next to
 * each control. Steps whose target isn't currently in the DOM (e.g. Pitch
 * / Sharps on legacy PDF projects without notehead data) are skipped.
 *
 * Self-contained — no tour dependency. The dim + cutout is a single
 * positioned div using an oversized `box-shadow` as the surround; a
 * transparent backdrop underneath swallows clicks so the page behind
 * stays inert while the tour is up.
 */
export const ProjectTutorial: React.FC<ProjectTutorialProps> = ({
  open,
  onClose,
  variant = 'pdf',
}) => {
  // Steps that actually have a target on screen, resolved when the tour
  // opens. Recomputed each open so toggled-off controls aren't pointed at.
  const [steps, setSteps] = useState<TutorialStep[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const scrollBodyRef = useRef<HTMLDivElement>(null);
  const [scrollMetrics, setScrollMetrics] = useState<ScrollMetrics>({
    overflow: false,
    canScrollDown: false,
    thumbTopPct: 0,
    thumbHeightPct: 100,
  });

  const currentStep = steps[stepIndex];
  // The card is not in the DOM until a spotlight rect exists (first open
  // starts with `rect === null`). Scroll measurement has to wait for that
  // commit — depending on the rect object itself would reset the body
  // scroll on every spotlight nudge.
  const cardVisible = open && !!currentStep && !!rect;

  const updateScrollMetrics = useCallback(() => {
    const el = scrollBodyRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const overflow = scrollHeight > clientHeight + 1;
    const maxScroll = Math.max(0, scrollHeight - clientHeight);
    const canScrollDown = overflow && scrollTop < maxScroll - 1;
    const thumbHeightPct = overflow
      ? Math.max(
          (SCROLL_THUMB_MIN / clientHeight) * 100,
          (clientHeight / scrollHeight) * 100
        )
      : 100;
    const travelPct = Math.max(0, 100 - thumbHeightPct);
    const thumbTopPct = maxScroll > 0 ? (scrollTop / maxScroll) * travelPct : 0;
    setScrollMetrics({ overflow, canScrollDown, thumbTopPct, thumbHeightPct });
  }, []);

  // Resolve the visible step list when the tour opens.
  useEffect(() => {
    if (!open) return;
    const visible = projectTutorialSteps(variant).filter((s) =>
      Boolean(measureTarget(s.target))
    );
    setSteps(visible);
    setStepIndex(0);
    if (visible.length === 0) {
      // Nothing to show (e.g. controls not mounted) — bail cleanly.
      onClose();
    }
  }, [open, onClose, variant]);

  // Keep the spotlight glued to the current target across layout shifts.
  useLayoutEffect(() => {
    if (!open || !currentStep) return;

    const update = () => setRect(measureTarget(currentStep.target));
    update();

    window.addEventListener('resize', update);
    // `true` (capture) so we catch scrolls on inner scrollers too.
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, currentStep, stepIndex]);

  // Reset scroll position + remeasure overflow whenever the step changes
  // (media/text length differs a lot between steps). Also when the card
  // first mounts: on the opening frame `rect` is still null, so the body
  // ref does not exist yet and a measure taken then sticks at "no overflow"
  // — the scroll-down arrow never appears until the next step.
  useLayoutEffect(() => {
    if (!cardVisible) return;
    const el = scrollBodyRef.current;
    if (el) el.scrollTop = 0;
    updateScrollMetrics();
  }, [cardVisible, stepIndex, currentStep, updateScrollMetrics]);

  // Keep the custom scrollbar in sync with body size changes (images
  // loading, orientation change, etc.).
  useEffect(() => {
    if (!cardVisible) return;
    const el = scrollBodyRef.current;
    if (!el) return;
    const onScroll = () => updateScrollMetrics();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(onScroll)
        : null;
    // Observe the scroller AND its content child — image loads grow
    // scrollHeight without changing the scroller's border box.
    ro?.observe(el);
    if (el.firstElementChild) ro?.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      ro?.disconnect();
    };
  }, [cardVisible, stepIndex, updateScrollMetrics]);

  const goNext = useCallback(() => {
    setStepIndex((i) => {
      if (i >= steps.length - 1) {
        onClose();
        return i;
      }
      return i + 1;
    });
  }, [steps.length, onClose]);

  const goBack = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  // Escape closes the tour.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !currentStep || !rect) return null;

  const isLast = stepIndex === steps.length - 1;
  const isFirst = stepIndex === 0;

  // Spotlight box (target + padding), clamped to stay on screen.
  const spotTop = rect.top - SPOTLIGHT_PAD;
  const spotLeft = rect.left - SPOTLIGHT_PAD;
  const spotW = rect.width + SPOTLIGHT_PAD * 2;
  const spotH = rect.height + SPOTLIGHT_PAD * 2;

  // Below `md` (768px) the tagged controls live in the fixed BOTTOM bar,
  // so the card must sit ABOVE them. Anchor it by its bottom edge, just
  // above the spotlight, and let it span the viewport (minus margins).
  const isMobile = window.innerWidth < 768;

  let cardStyle: React.CSSProperties;
  if (isMobile) {
    cardStyle = {
      left: VIEWPORT_MARGIN,
      right: VIEWPORT_MARGIN,
      // Distance from the viewport bottom up to just above the spotlight.
      bottom: window.innerHeight - spotTop + CARD_GAP,
    };
  } else {
    // Place the card to whichever side has more room: targets in the right
    // gutter (side toggles) get a card on their LEFT; top-left controls get
    // a card on their RIGHT / BELOW.
    const targetCenterX = rect.left + rect.width / 2;
    const placeLeft = targetCenterX > window.innerWidth / 2;

    let cardLeft = placeLeft
      ? spotLeft - CARD_GAP - CARD_WIDTH
      : spotLeft + spotW + CARD_GAP;

    // If horizontal placement spills off-screen, fall back to below the
    // target, horizontally clamped.
    let cardTop = spotTop;
    if (cardLeft < VIEWPORT_MARGIN) {
      cardLeft = VIEWPORT_MARGIN;
      cardTop = spotTop + spotH + CARD_GAP;
    } else if (cardLeft + CARD_WIDTH > window.innerWidth - VIEWPORT_MARGIN) {
      cardLeft = window.innerWidth - VIEWPORT_MARGIN - CARD_WIDTH;
      cardTop = spotTop + spotH + CARD_GAP;
    }

    if (cardTop < VIEWPORT_MARGIN) cardTop = VIEWPORT_MARGIN;

    // Fit the card in the viewport so Skip/Next stay on screen. Inline
    // maxHeight is the tighter of the Tailwind ceiling (70vh / 40rem) and
    // the space left below cardTop. A right-gutter pill on a short window
    // otherwise drops the footer below the fold, and the fixed overlay
    // blocks page scroll so it cannot be reached.
    const viewportMax = Math.min(
      window.innerHeight * CARD_MAX_HEIGHT_VH,
      CARD_MAX_HEIGHT_PX,
      window.innerHeight - 2 * VIEWPORT_MARGIN
    );
    const minHeight = Math.min(CARD_MIN_HEIGHT, viewportMax);
    const roomBelow = window.innerHeight - VIEWPORT_MARGIN - cardTop;
    if (roomBelow < minHeight) {
      cardTop = Math.max(
        VIEWPORT_MARGIN,
        window.innerHeight - VIEWPORT_MARGIN - minHeight
      );
    }
    const maxHeight = Math.max(
      0,
      Math.min(viewportMax, window.innerHeight - VIEWPORT_MARGIN - cardTop)
    );

    cardStyle = { top: cardTop, left: cardLeft, maxHeight };
  }

  return (
    <>
      {/* Transparent click-catcher so the page behind stays inert. */}
      <div
        className="fixed inset-0 z-[60]"
        aria-hidden
        onClick={(e) => e.stopPropagation()}
      />

      {/* Spotlight: the oversized box-shadow paints the dim surround over
          everything (toolbar z-50, side toggles z-40), leaving the target
          area bright with a soft ring. */}
      <div
        className={`
          pointer-events-none fixed z-[61] rounded-xl transition-all
          duration-200 ease-out
        `}
        style={{
          top: spotTop,
          left: spotLeft,
          width: spotW,
          height: spotH,
          boxShadow:
            '0 0 0 9999px rgba(17, 24, 39, 0.6), 0 0 0 2px rgba(92, 107, 255, 0.9)',
        }}
      />

      {/* Explanatory card. Header + footer stay pinned; only the body
          scrolls so Skip/Next never disappear under tall media steps.
          A custom track/thumb is drawn on mobile because iOS hides the
          native overlay scrollbar when idle. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Tutorial: ${currentStep.title}`}
        className={`
          fixed z-[62] flex max-h-[70vh] flex-col overflow-hidden rounded-2xl
          bg-white shadow-2xl ring-1 ring-black/10
          md:max-h-[min(70vh,40rem)] md:w-[24rem]
        `}
        style={cardStyle}
      >
        <div
          className={`
            flex shrink-0 items-start justify-between gap-x-2 px-5 pt-5
          `}
        >
          <p className="text-xs font-medium tracking-wide text-[#5C6BFF]">
            Step {stepIndex + 1} of {steps.length}
          </p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close tutorial"
            className={`
              -mt-1 -mr-1 flex h-7 w-7 cursor-pointer items-center
              justify-center rounded-full text-gray-400 transition-colors
              hover:bg-gray-100 hover:text-gray-600
            `}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
          <div
            ref={scrollBodyRef}
            className={`
              tutorial-card-scroll min-h-0 flex-1 overflow-y-auto px-5 py-3 pr-4
            `}
          >
            <div className="flex flex-col gap-y-3 pr-2">
              <h2 className="text-lg font-bold text-gray-900">
                {currentStep.title}
              </h2>

              {/* Media slot. A `mediaRow` lays images left-to-right (e.g. the
                  edit drag/swap actions). A `mediaSequence` (toggle /
                  progression steps) renders each image stacked vertically with
                  a vertical toggle arrow between consecutive pairs. A single
                  `media` renders as an image/video. Steps with no media (e.g.
                  Download) render nothing here and rely on their text +
                  bullets. */}
              {currentStep.mediaRow ? (
                <div className="flex w-full items-stretch gap-x-2">
                  {currentStep.mediaRow.map((m) => (
                    <img
                      key={m.src}
                      src={m.src}
                      alt={m.alt ?? ''}
                      className={`
                        h-28 w-0 flex-1 rounded border border-gray-200
                        object-contain
                      `}
                      draggable={false}
                    />
                  ))}
                </div>
              ) : currentStep.mediaSequence ? (
                <div className="flex w-full flex-col items-center gap-y-2">
                  {currentStep.mediaSequence.map((m, i) => (
                    <React.Fragment key={m.src}>
                      {i > 0 && (
                        <FontAwesomeIcon
                          icon={faArrowRightArrowLeft}
                          rotation={90}
                          className="text-gray-500"
                        />
                      )}
                      <img
                        src={m.src}
                        alt={m.alt ?? ''}
                        className={`
                          h-28 w-auto rounded border border-gray-200
                          object-contain
                        `}
                        draggable={false}
                      />
                    </React.Fragment>
                  ))}
                </div>
              ) : currentStep.media ? (
                <div
                  className={`
                    flex h-28 w-full items-center justify-center overflow-hidden
                    rounded-lg bg-gray-100
                  `}
                >
                  {isVideo(currentStep.media) ? (
                    <video
                      src={currentStep.media}
                      className="h-full w-full object-contain"
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                  ) : (
                    <img
                      src={currentStep.media}
                      alt={currentStep.mediaAlt ?? ''}
                      className="max-h-20 max-w-[60%] object-contain"
                      draggable={false}
                    />
                  )}
                </div>
              ) : null}

              <p className="text-sm leading-relaxed text-gray-600">
                {currentStep.description}
              </p>

              {currentStep.actions && (
                <ul className="list-disc space-y-1 pl-5 text-sm text-gray-600">
                  {currentStep.actions.map((a) => (
                    <li key={a}>{a}</li>
                  ))}
                </ul>
              )}

              {currentStep.bulletsIntro && (
                <p className="text-sm leading-relaxed text-gray-600">
                  {currentStep.bulletsIntro}
                </p>
              )}

              {currentStep.bullets && (
                <ul className="flex flex-col gap-y-2">
                  {currentStep.bullets.map((b) => (
                    <li
                      key={b.label}
                      className="flex items-start gap-x-2 text-sm"
                    >
                      <FontAwesomeIcon
                        icon={b.icon}
                        className="mt-0.5 text-gray-500"
                      />
                      <span className="text-gray-600">
                        <strong className="text-gray-800">{b.label}</strong> —{' '}
                        {b.text}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Always-visible custom scrollbar — iOS never keeps the native
              overlay bar on screen, so we paint our own track + thumb. */}
          {scrollMetrics.overflow && (
            <div
              className={`
                pointer-events-none absolute top-2 right-1.5 bottom-2 w-1.5
                rounded-full bg-gray-200
              `}
              aria-hidden
            >
              <div
                className="absolute inset-x-0 rounded-full bg-gray-500"
                style={{
                  top: `${scrollMetrics.thumbTopPct}%`,
                  height: `${scrollMetrics.thumbHeightPct}%`,
                }}
              />
            </div>
          )}

          {/* Bottom fade + chevron so overflow is obvious before the user
              starts dragging. */}
          {scrollMetrics.canScrollDown && (
            <div
              className={`
                pointer-events-none absolute inset-x-0 bottom-0 flex h-10
                items-end justify-center bg-gradient-to-t from-white
                via-white/90 to-transparent pb-0.5
              `}
              aria-hidden
            >
              <FontAwesomeIcon
                icon={faChevronDown}
                className="animate-bounce text-sm text-gray-400"
              />
            </div>
          )}
        </div>

        <div
          className={`
            flex shrink-0 items-center justify-between border-t border-gray-100
            px-5 py-3
          `}
        >
          <button
            type="button"
            onClick={onClose}
            className={`
              cursor-pointer text-sm font-medium text-gray-400 transition-colors
              hover:text-gray-600
            `}
          >
            Skip tour
          </button>
          <div className="flex items-center gap-x-2">
            {!isFirst && (
              <button
                type="button"
                onClick={goBack}
                className={`
                  cursor-pointer rounded-full border border-gray-800
                  bg-transparent px-4 py-2 text-sm font-medium text-gray-800
                  transition-colors
                  hover:bg-gray-800/5
                `}
              >
                Back
              </button>
            )}
            <button
              type="button"
              onClick={goNext}
              className={`
                cursor-pointer rounded-full bg-gray-800 px-5 py-2 text-sm
                font-medium text-white transition-colors
                hover:bg-gray-700
              `}
            >
              {isLast ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
